import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

import Img from '../../../../../components/Img';
import BackgroundImage from '../../../../../components/BackgroundImage';
import Utr from '../../../../../components/text/Utr';
import { getFlagUrl } from '../../../../../utils';
import { DEFAULT_PLAYER_NAME } from '../../../../../utils/constants';
import { titleCase } from '../../../../../utils/formatting';
import { gameTypeValues, eventFormatTypeIds } from '../../../../../utils/eventConstants';
import { getValueByTypeId } from '../../../../../utils/types';
import { formatPlayerNameByStatus } from '../../../../../utils/matchCardUtils';

import styles from './eventProfileMatchCard.scss';
import { getPlayerHSGradStatus } from '../../../../../utils/draws';

const PlayerRowContent = (props) => {
    const {
        playerData,
        shortenFirstName = false,
        seed,
        tag,
        showRatings,
        sportTypeId,
        isProgressionDraw,
        formatTypeId,
        utrTypeId,
        utrTypes = [],
        isOfficialHSEvent,
        isCurrentMemberAdmin,
        hasPositionWon,
    } = props;

    const auth = useSelector(({ auth }) => auth);

    const ratingType = formatTypeId === eventFormatTypeIds.SINGLES ? gameTypeValues.SINGLES : gameTypeValues.DOUBLES;
    const utrType = utrTypeId ? getValueByTypeId(utrTypes, utrTypeId) : null;

    if (!playerData) {
        return null;
    }

    const { player } = playerData;
    const playerName = player
        ? formatPlayerNameByStatus(player, shortenFirstName)
        : (playerData?.name || DEFAULT_PLAYER_NAME);

    const hasClubProfileImages = player?.clubProfileImages;
    const isTBDOrBye = playerData.name === DEFAULT_PLAYER_NAME || playerData.name === 'Bye';
    const displayName = (playerName === DEFAULT_PLAYER_NAME || playerName === 'Bye') ? playerName : titleCase(playerName);
    const nationality = playerData.nationality ?? player?.nationality;

    const showDecimalsForPlayer = (player) => {
        return isCurrentMemberAdmin || (auth.user?.claimedPlayer && player?.playerId === auth.user.claimedPlayer.playerId);
    };

    const highSchoolDataItems = [
        ...(isTBDOrBye ? [] : [getPlayerHSGradStatus(player)]),
        ...(isTBDOrBye ? [] : [player?.highSchool]),
    ].filter(Boolean);
    const shouldDisplayHighSchoolInfo = isOfficialHSEvent && highSchoolDataItems.length > 0;

    return (
        <>
            <div
                className={`${styles.playerRow} ${hasPositionWon ? styles.winner : ''}`}
            >
                {!isTBDOrBye && seed != null && seed !== '' && (
                    <span className={styles.seed}>{seed}</span>
                )}
                {!isTBDOrBye && nationality && (
                    <Img
                        className={styles.flagContainer}
                        src={getFlagUrl(nationality)}
                        alt={nationality}
                        title={nationality}
                    />
                )}
                {!isTBDOrBye && hasClubProfileImages && (
                    <BackgroundImage
                        baseUrl="/v1/club/"
                        defaultImage="/img/utr-ball--teal.svg"
                        images={player.clubProfileImages}
                        imageType="thumbnail"
                        parentStylesClass={styles.flagContainer}
                    />
                )}
                {!isTBDOrBye && !nationality && !hasClubProfileImages && (
                    <span className={styles.flagContainer} />
                )}
                {player?.playerId ? (
                    <Link to={`/profiles/${player.playerId}`} className={styles.playerName}>
                        {displayName}
                    </Link>
                ) : (
                    <span className={`${styles.playerName} ${(playerName === DEFAULT_PLAYER_NAME || playerName === 'Bye') ? styles.tbdOrBye : ''}`}>
                        {displayName}
                    </span>
                )}
                {tag && (
                    <span className={styles.playerTag}>({String(tag).toUpperCase()})</span>
                )}
                {showRatings && player && (
                    <div className={styles.utrContainer}>
                        <Utr
                            sportTypeId={sportTypeId}
                            pbrClass={styles.pbr}
                            player={player}
                            isProgressionDraw={isProgressionDraw}
                            force={{
                                ratingType,
                                utrType,
                                showDecimals: showDecimalsForPlayer(player),
                            }}
                        />
                    </div>
                )}
            </div>
            {shouldDisplayHighSchoolInfo && (
                <div className={`${styles.playerRow} ${styles.highSchoolData}`}>
                    {seed != null && seed !== '' && (
                        <span className={styles.seed} />
                    )}
                    <span className={styles.flagContainer} />
                    <span>
                        {highSchoolDataItems.join(' \u2022 ')}
                    </span>
                </div>
            )}
        </>
    );
};

PlayerRowContent.propTypes = {
    playerData: PropTypes.shape({
        name: PropTypes.string,
        nationality: PropTypes.string,
        player: PropTypes.object,
    }),
    shortenFirstName: PropTypes.bool,
    seed: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tag: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    showRatings: PropTypes.bool.isRequired,
    sportTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isProgressionDraw: PropTypes.bool.isRequired,
    formatTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    utrTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    utrTypes: PropTypes.arrayOf(PropTypes.object),
    isOfficialHSEvent: PropTypes.bool,
    isCurrentMemberAdmin: PropTypes.bool,
    hasPositionWon: PropTypes.bool,
};

PlayerRowContent.defaultProps = {
    playerData: null,
    shortenFirstName: false,
    seed: null,
    tag: null,
    sportTypeId: null,
    formatTypeId: null,
    utrTypeId: null,
    utrTypes: [],
    isOfficialHSEvent: false,
    isCurrentMemberAdmin: false,
    hasPositionWon: false,
};

export default PlayerRowContent;
